import { useState } from "react";
import { Shuffle } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { ACCENT_PALETTE } from "~/lib/accentPalette";
import { SettingsSection } from "~/pages/manifold-nextgen/components/settings/SettingsSection";
import {
  AGENT_BRAND_COLORS,
  Agent,
  agentPath,
  mutateAgentList,
  updateAgent,
} from "~/services/agents";
import {
  AgentAvatar,
  agentAvatarSeed,
  allShapeSeeds,
  resolveAgentAvatarColor,
  resolveShapeVariant,
} from "./AgentAvatar";

interface AgentAppearanceSectionProps {
  agent: Agent;
}

const initialAvatar = (agent: Agent) => {
  const seed = agentAvatarSeed(agent);
  return { seed, color: resolveAgentAvatarColor(seed, agent.avatar_color) };
};

export const AgentAppearanceSection = ({ agent }: AgentAppearanceSectionProps) => {
  const { showSuccess, showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // The last-saved seed/color, used both as the Reset target and to decide whether the
  // form is dirty. Updated on a successful save so Save/Reset re-disable immediately,
  // without waiting on the parent to re-fetch and pass down a fresh `agent` prop.
  const [committed, setCommitted] = useState(() => initialAvatar(agent));

  const [seed, setSeed] = useState(committed.seed);
  const [color, setColor] = useState(committed.color);

  const isDirty = seed !== committed.seed || color !== committed.color;

  const shapeSeeds = allShapeSeeds();
  const currentShapeVariant = resolveShapeVariant(seed);

  const handleRandomize = () => {
    const newSeed = shapeSeeds[Math.floor(Math.random() * shapeSeeds.length)];
    setColor(AGENT_BRAND_COLORS[Math.floor(Math.random() * AGENT_BRAND_COLORS.length)]);
    setSeed(newSeed);
  };

  const handleReset = () => {
    setSeed(committed.seed);
    setColor(committed.color);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateAgent(agent.id, { avatar_seed: seed, avatar_color: color });
      setCommitted({ seed, color });
      await mutate(agentPath(agent.id));
      await mutateAgentList();
      showSuccess("Avatar saved");
    } catch {
      showError("Failed to save avatar");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingsSection title="Appearance">
      <div className="grid grid-cols-[280px_1fr] gap-8 px-4 py-4">
        <div className="flex flex-col items-center gap-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Preview
          </span>
          <AgentAvatar agent={{ avatar_seed: seed, avatar_color: color }} size={96} />
          <Button variant="outline" size="sm" onClick={handleRandomize} className="w-full">
            <Shuffle className="size-4" />
            Randomize
          </Button>
        </div>

        <div className="flex flex-col gap-5">
          <div>
            <p className="mb-2 text-sm font-medium text-foreground">Color</p>
            <div className="flex flex-wrap gap-2.5">
              {ACCENT_PALETTE.map((swatch) => (
                <button
                  key={swatch.accent}
                  type="button"
                  aria-label={`Use color ${swatch.accent}`}
                  aria-pressed={color === swatch.accent}
                  onClick={() => setColor(swatch.accent)}
                  className="size-7 cursor-pointer rounded-full border-2"
                  style={{
                    backgroundColor: swatch.bg,
                    borderColor: swatch.accent,
                    boxShadow:
                      color === swatch.accent
                        ? `0 0 0 2px var(--background), 0 0 0 4px ${swatch.accent}`
                        : undefined,
                  }}
                />
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-foreground">Shape</p>
            <div className="flex flex-wrap gap-2.5">
              {shapeSeeds.map((sampleSeed, index) => {
                const isSelected = currentShapeVariant === resolveShapeVariant(sampleSeed);
                return (
                  <button
                    key={sampleSeed}
                    type="button"
                    aria-label={`Shape option ${index + 1}`}
                    aria-pressed={isSelected}
                    onClick={() => setSeed(sampleSeed)}
                    className="size-7 shrink-0 cursor-pointer rounded-full p-0"
                    style={{
                      boxShadow: isSelected
                        ? `0 0 0 2px var(--background), 0 0 0 4px ${color}`
                        : "0 0 0 1px var(--border)",
                    }}
                  >
                    <AgentAvatar
                      agent={{ avatar_seed: sampleSeed, avatar_color: color }}
                      size={28}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 px-4 py-3">
        <Button variant="ghost" onClick={handleReset} disabled={!isDirty || isSaving}>
          Reset
        </Button>
        <Button onClick={handleSave} disabled={!isDirty || isSaving} loading={isSaving}>
          Save
        </Button>
      </div>
    </SettingsSection>
  );
};
